/**
 * aramFowOverlay — Pure FoW overlay params + clear-disk list (SPY-14).
 * Presentation reads opacity; domain owns the readable range contract.
 */

import { wardRemainingSec } from '../vision.js';

/** Mid opacity so Murder Bridge fog stays legible without crushing the deck. */
export const ARAM_FOW_FOG_OPACITY = 0.42;

/** Clear-disk fill — soft lighten inside sight (additive in presentation). */
export const ARAM_FOW_CLEAR_OPACITY = 0.22;

/** Y lift so fog sits above the bridge deck. */
export const ARAM_FOW_Y = 0.12;

/** Last seconds where ward FoW disk shrinks linearly (SPY-406). */
export const WARD_FOW_FADE_SEC = 30;

/** Team tint tokens for FoW clear disks (SPY-506). */
export const WARD_FOW_TEAM_TINT = Object.freeze({
  blue: '#3a7ebd',
  red: '#c44a4a'
});

/** Disk opacity floor during fade window (SPY-1106 → SPY-1206). */
export const WARD_FOW_DISK_OPACITY_FLOOR = 0.12;
/** Sharper opacity fade in last seconds (SPY-1106 → SPY-1206). */
export const WARD_FOW_DISK_TIGHTEN_SEC = 5;

/**
 * @param {string} [team]
 * @returns {string}
 */
export function wardFowDiskTeamTint(team) {
  if (team === 'red') return WARD_FOW_TEAM_TINT.red;
  return WARD_FOW_TEAM_TINT.blue;
}

/**
 * Ward FoW clear-disk radius from remainingSec (full until fade window).
 * @param {{ visionRadius?: number, expiresAt?: number }|null} ward
 * @param {number|null} gameTime
 */
export function wardFowDiskRadius(ward, gameTime = null) {
  if (!ward) return 0;
  const base = Number(ward.visionRadius) || 0;
  if (!(base > 0)) return 0;
  if (!Number.isFinite(gameTime)) return base;
  const rem = wardRemainingSec(ward, gameTime);
  if (rem <= 0) return 0;
  if (rem >= WARD_FOW_FADE_SEC) return base;
  return Math.round(base * (rem / WARD_FOW_FADE_SEC) * 100) / 100;
}

/**
 * Ward FoW clear-disk opacity fades with remainingSec (SPY-606).
 * @param {{ expiresAt?: number }|null} ward
 * @param {number|null} gameTime
 */
export function wardFowDiskOpacity(ward, gameTime = null) {
  if (!ward) return 0;
  if (!Number.isFinite(gameTime)) return 1;
  const rem = wardRemainingSec(ward, gameTime);
  if (rem <= 0) return 0;
  if (rem >= WARD_FOW_FADE_SEC) return 1;
  const t = rem / WARD_FOW_FADE_SEC;
  let opacity = WARD_FOW_DISK_OPACITY_FLOOR + (1 - WARD_FOW_DISK_OPACITY_FLOOR) * t;
  if (rem <= WARD_FOW_DISK_TIGHTEN_SEC) {
    const tighten = rem / WARD_FOW_DISK_TIGHTEN_SEC;
    opacity *= 0.55 + 0.45 * tighten;
  }
  return Math.round(Math.max(WARD_FOW_DISK_OPACITY_FLOOR, opacity) * 100) / 100;
}

function pushUnitClearDisk(disks, unit, source, team) {
  if (!unit || unit.isDead) return;
  if (typeof unit.visionRadius !== 'number') return;
  if (unit.team && unit.team !== team) return;
  disks.push({
    x: unit.x,
    z: unit.z ?? unit.y ?? 0,
    radius: unit.visionRadius,
    source,
    tint: wardFowDiskTeamTint(unit.team || team)
  });
}

/**
 * listAramFowClearDisks — viewer + allied unit + ward vision circles.
 */
export function listAramFowClearDisks(
  viewer,
  activeWards = [],
  team = 'blue',
  gameTime = null,
  alliedViewers = []
) {
  const disks = [];
  pushUnitClearDisk(disks, viewer, 'self', viewer?.team || team);
  for (const ally of alliedViewers) {
    if (ally === viewer) continue;
    pushUnitClearDisk(disks, ally, 'ally', team);
  }
  for (const ward of activeWards) {
    if (!ward || ward.team !== team) continue;
    const radius = wardFowDiskRadius(ward, gameTime);
    if (!(radius > 0)) continue;
    disks.push({
      x: ward.x,
      z: ward.z ?? ward.y ?? 0,
      radius,
      opacity: wardFowDiskOpacity(ward, gameTime),
      source: 'ward',
      tint: wardFowDiskTeamTint(ward.team)
    });
  }
  return disks;
}

/**
 * buildAramFowDebugSnapshot — agent smoke / `__debugFowState` payload.
 */
export function buildAramFowDebugSnapshot({
  viewer = null,
  wallBoxes = [],
  activeWards = [],
  team = 'blue',
  gameTime = null,
  alliedViewers = []
} = {}) {
  return {
    fogOpacity: ARAM_FOW_FOG_OPACITY,
    clearOpacity: ARAM_FOW_CLEAR_OPACITY,
    visionRadius: viewer?.visionRadius ?? null,
    losBlockerCount: (wallBoxes || []).length,
    clearDisks: listAramFowClearDisks(viewer, activeWards, team, gameTime, alliedViewers)
  };
}
